// ============================================================================
// DUST — single source of truth for "hide sub-min-order residuals on Active"
// ============================================================================
// A position is "dust" when it's a residual you can't trade out of: Polymarket's
// min order is ~$1, so anything under 1 share, or ≥1 share but worth AND costing
// under $1, can never be re-sold and just clutters the Active view.
//
// Used by:
//   • the per-source summaries (useUnifiedPositions) → counts + value, which in
//     turn drive the profile-bar "N open" stat AND the wallet cards (so they
//     never disagree — that disagreement was the zeroxitadori ghost-position bug);
//   • the Active list filter (PositionsSection).
//
// Mirrors the TG bot's isDustPosition (telegram-ui/src/bot/commands/positions.ts)
// and the backend's DUST_RESOLUTION_USD (backend/src/lib/sse-events.ts).
// Keep all three at $1.
export const DUST_USD = 1;

export function isActiveDust(p: { shares: number; currentValue: number; invested: number }): boolean {
  const shares = Number(p.shares) || 0;
  if (shares <= 0) return false; // closed / zero-balance handled by isOpen, not here
  if (shares < 1) return true; // CLOB rounding leftover (e.g. 17.79 sold → 0.79 stuck)
  // ≥1 share but immaterial: value AND cost both sub-$1. Cost < $1 means it's a
  // leftover (you can't OPEN a sub-$1 position), never an intentional small bet.
  // A real position that decayed keeps cost ≥ $1 and stays visible (never hide a loss).
  return (Number(p.currentValue) || 0) < DUST_USD && (Number(p.invested) || 0) < DUST_USD;
}

// Closed-tab dust: ONLY structural rounding residue — a <1-share leftover with
// ≈$0 everywhere (value, realized AND total P&L), i.e. a "sell all" remainder
// that was never an independent position. Everything with a real outcome — any
// non-zero realized / P&L, any value, or ≥1 share — is a finished position
// (sold / won / lost) and the Closed tab MUST show it (its title literally
// promises "sold, won or lost").
//
// NOTE: deliberately NOT a sub-$1 gate. The old `value<$1 && |realized|<$1 &&
// |totalPnL|<$1` rule hid 78/137 of zeroxitadori's closed rows — incl. 29 real
// losses and manual sub-$1 sells — which contradicted the tab's own title.
// Single source of truth shared by the Closed list AND the "N closed" stat.
export function isClosedDust(p: { shares: number; currentValue: number; realizedPnL?: number; totalPnL?: number }): boolean {
  const shares = Number(p.shares) || 0;
  const curVal = Number(p.currentValue) || 0;
  const realized = Number(p.realizedPnL) || 0;
  const totalPnl = Number(p.totalPnL) || 0;
  return shares > 0 && shares < 1 && curVal < 0.01 && Math.abs(totalPnl) < 0.01 && Math.abs(realized) < 0.01;
}
